import type {
	ComponentContent,
	ComponentSlot,
} from 'store/ui';
import {
	defineComponent,
	h,
	type PropType,
	type VNodeChild,
} from 'vue';
import ComponentSlotRenderer from '../component-slot-renderer';
import {
	normalizeComponentSlots,
	resolveComponentValue,
} from '../helpers';

/**
 * Renders global UI content from either Vue slot functions or structured slot data.
 *
 * @param content - Content stored by imperative callers or renderless lifecycle wrappers.
 * @returns Renderable Vue children for the global UI host.
 */
function renderComponentContent(content: ComponentContent | ComponentSlot['slot'] | undefined): VNodeChild {
	const resolvedContent = resolveComponentValue(content);

	if (!resolvedContent) {
		return null;
	}

	if (typeof resolvedContent === 'function') {
		return resolvedContent();
	}

	if (typeof resolvedContent === 'string') {
		return resolvedContent;
	}

	return normalizeComponentSlots(content).map((item, index) => h(
		ComponentSlotRenderer,
		{
			item,
			key: index,
		},
	));
}

export default defineComponent({
	name: 'ComponentContentRenderer',
	props: {
		content: {
			default: undefined,
			type: null as unknown as PropType<ComponentContent | ComponentSlot['slot'] | undefined>,
		},
	},
	setup(props) {
		return () => renderComponentContent(props.content);
	},
});
